// TransformGizmo — 3 стрелки (X красная, Y зелёная, Z синяя) у текущей
// pendingFeature.position. Drag за стрелку → меняет position тела.
//
// Используется когда pendingFeature.kind === 'primitive' и есть bodyId.
//
// Архитектура (важно — иначе drag прерывается на ре-рендерах):
//   - position-state, gizmo-position и pointer-listeners живут в ref'ах,
//     React-рендер делает только начальное создание group и стрелок.
//   - Тело перерисовывается напрямую через window.__kubikonCadLivePreview()
//     (callback ставит PrimitiveForm) — без debounce, real-time.
//   - В store position записываем только на pointer-up.

import React, { useRef, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useCadStore from '../store/useCadStore';

const ARROW_LEN = 35;
const ARROW_RADIUS = 0.8;
const HEAD_LEN = 8;
const HEAD_RADIUS = 2.6;
const TOTAL_LEN = ARROW_LEN + HEAD_LEN;


// Геометрия стрелки (стержень + наконечник) предсчитанная единожды на ось.
// Ось ориентирована вдоль +Y; снаружи поворачиваем quaternion'ом.
function makeArrowGroupContent(scene, color) {
    const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(ARROW_RADIUS, ARROW_RADIUS, ARROW_LEN, 12),
        new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 }),
    );
    stem.position.set(0, ARROW_LEN / 2, 0);
    const head = new THREE.Mesh(
        new THREE.ConeGeometry(HEAD_RADIUS, HEAD_LEN, 16),
        new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 }),
    );
    head.position.set(0, ARROW_LEN + HEAD_LEN / 2, 0);
    return { stem, head };
}


const AXIS_DEFS = [
    { idx: 0, color: '#cc3344', hover: '#ff5566' },
    { idx: 1, color: '#0c8',    hover: '#3da' },
    { idx: 2, color: '#3a78ff', hover: '#5a98ff' },
];


const TransformGizmo = () => {
    const { camera, gl, controls, scene } = useThree();
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const isPrim = pendingFeature?.kind === 'primitive' && !!pendingFeature.bodyId;
    const isMove = pendingFeature?.kind === 'moveCopy' && !!pendingFeature.bodyId;
    const isActive = isPrim || isMove;

    // Group в three-сцене (создаётся один раз и потом просто двигается).
    // НЕ React-state, чтобы drag не прерывался ре-рендерами.
    const groupRef = useRef(null);
    const arrowsRef = useRef([]);  // [{group, stem, head, idx, materials, hovered}]
    const ringsRef = useRef([]);   // [{mesh, idx, material, color, hover}] — кольца поворота
    const positionRef = useRef([0, 0, 0]);
    const draggingRef = useRef(null);
    // Линия-вектор от стартовой точки drag'а до текущей (белая, тонкая).
    // Обновляется imperative как и group.
    const dragLineRef = useRef(null);

    // Создаём group + стрелки один раз
    useEffect(() => {
        if (groupRef.current) return;
        const g = new THREE.Group();
        g.renderOrder = 9999;
        scene.add(g);
        groupRef.current = g;

        for (const ax of AXIS_DEFS) {
            const arrowGroup = new THREE.Group();
            const { stem, head } = makeArrowGroupContent(scene, ax.color);
            arrowGroup.add(stem);
            arrowGroup.add(head);

            // Quaternion: поворот +Y → ось axis
            const from = new THREE.Vector3(0, 1, 0);
            const target = new THREE.Vector3(
                ax.idx === 0 ? 1 : 0,
                ax.idx === 1 ? 1 : 0,
                ax.idx === 2 ? 1 : 0,
            );
            arrowGroup.quaternion.setFromUnitVectors(from, target);
            // Помечаем для raycaster — ловим клики через onPointerDown сцены
            arrowGroup.userData.kubikonAxisIdx = ax.idx;
            stem.userData.kubikonAxisIdx = ax.idx;
            head.userData.kubikonAxisIdx = ax.idx;

            g.add(arrowGroup);
            arrowsRef.current.push({
                group: arrowGroup, stem, head,
                idx: ax.idx, color: ax.color, hover: ax.hover,
                materials: [stem.material, head.material],
            });
        }

        // Кольца поворота (torus) вокруг каждой оси. Только для moveCopy;
        // видимость переключается в useEffect ниже. Радиус чуть меньше стрелок.
        const RING_R = ARROW_LEN * 0.72;
        for (const ax of AXIS_DEFS) {
            const ringGeom = new THREE.TorusGeometry(RING_R, 0.7, 8, 48);
            const ringMat = new THREE.MeshBasicMaterial({
                color: ax.color, depthTest: false, transparent: true, opacity: 0.9,
            });
            const ring = new THREE.Mesh(ringGeom, ringMat);
            // Torus по умолчанию в плоскости XY (нормаль +Z). Ориентируем так,
            // чтобы нормаль кольца совпала с осью idx.
            if (ax.idx === 0) ring.rotation.y = Math.PI / 2;      // нормаль X
            else if (ax.idx === 1) ring.rotation.x = Math.PI / 2; // нормаль Y
            // idx 2 — нормаль уже Z, поворот не нужен.
            ring.userData.kubikonRingIdx = ax.idx;
            g.add(ring);
            ringsRef.current.push({
                mesh: ring, idx: ax.idx, material: ringMat,
                color: ax.color, hover: ax.hover,
            });
        }

        // Линия-вектор drag'а — белая тонкая, лежит в scene (не в g —
        // потому что у g меняется position при drag, а линия должна
        // строиться от стартовой world-точки до текущей).
        const lineGeom = new THREE.BufferGeometry();
        lineGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
        const lineMat = new THREE.LineDashedMaterial({
            color: '#ffffff',
            depthTest: false,
            transparent: true,
            opacity: 0.85,
            dashSize: 3,
            gapSize: 2,
        });
        const line = new THREE.Line(lineGeom, lineMat);
        line.frustumCulled = false;
        line.visible = false;
        line.renderOrder = 9998;
        scene.add(line);
        dragLineRef.current = line;

        return () => {
            scene.remove(g);
            scene.remove(line);
            line.geometry.dispose();
            line.material.dispose();
            for (const a of arrowsRef.current) {
                a.stem.geometry.dispose();
                a.stem.material.dispose();
                a.head.geometry.dispose();
                a.head.material.dispose();
            }
            for (const r of ringsRef.current) {
                r.mesh.geometry.dispose();
                r.mesh.material.dispose();
            }
            arrowsRef.current = [];
            ringsRef.current = [];
            groupRef.current = null;
            dragLineRef.current = null;
        };
    }, [scene]);

    // Видимость + позиция group зависит от pendingFeature.
    //
    // Для moveCopy используем pendingFeature.initialCenter (зафиксированный
    // bbox-центр на момент старта операции). НЕ пересчитываем от bodyMeshes —
    // body двигается при live-preview, его bbox-центр меняется, и если бы
    // gizmo опирался на текущий центр, получился бы рассинхрон: при drag
    // позиция считалась бы как (новый_центр + dx), а новый_центр уже сдвинут
    // на предыдущий dx — это и есть «дёрганье вдоль оси».
    //
    // Также во время drag НЕ переопределяем positionRef из useEffect:
    // pointer-handler ставит g.position синхронно при move, и любая
    // последующая запись из useEffect перебьёт правильное значение
    // и кратковременно вернёт gizmo в стартовую точку.
    useEffect(() => {
        const g = groupRef.current;
        if (!g) return;
        if (!isActive) {
            g.visible = false;
            return;
        }
        g.visible = true;
        // Кольца поворота — только в режиме перемещения/копирования тела
        // (у примитива вращения нет, там только позиция).
        for (const r of ringsRef.current) r.mesh.visible = isMove;
        // Если идёт drag — pointer-handler уже управляет позицией. Не лезем.
        if (draggingRef.current) return;
        let pos;
        if (isPrim) {
            pos = pendingFeature.position || [0, 0, 0];
        } else {
            const c = pendingFeature.initialCenter || [0, 0, 0];
            pos = [
                c[0] + (pendingFeature.dx || 0),
                c[1] + (pendingFeature.dy || 0),
                c[2] + (pendingFeature.dz || 0),
            ];
        }
        positionRef.current = [...pos];
        g.position.set(pos[0], pos[1], pos[2]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isActive, isPrim, isMove,
        pendingFeature?.position,
        pendingFeature?.bodyId,
        pendingFeature?.dx, pendingFeature?.dy, pendingFeature?.dz,
        pendingFeature?.initialCenter,
    ]);

    // === Pointer handlers — на DOM canvas, через raycaster ===
    useEffect(() => {
        const dom = gl.domElement;
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();

        const hitTestArrow = (clientX, clientY) => {
            const rect = dom.getBoundingClientRect();
            mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(mouse, camera);
            const meshes = [];
            for (const a of arrowsRef.current) {
                meshes.push(a.stem, a.head);
            }
            const hits = raycaster.intersectObjects(meshes, false);
            if (hits.length === 0) return null;
            return hits[0].object.userData.kubikonAxisIdx ?? null;
        };

        // Hit-test кольца поворота. Возвращает { ringIdx } или null.
        const hitTestRing = (clientX, clientY) => {
            const rect = dom.getBoundingClientRect();
            mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(mouse, camera);
            const meshes = ringsRef.current.filter((r) => r.mesh.visible).map((r) => r.mesh);
            const hits = raycaster.intersectObjects(meshes, false);
            if (hits.length === 0) return null;
            return hits[0].object.userData.kubikonRingIdx ?? null;
        };

        const setHovered = (axisIdx) => {
            for (const a of arrowsRef.current) {
                const targetColor = (a.idx === axisIdx) ? a.hover : a.color;
                for (const m of a.materials) {
                    if (m.color.getHexString() !== new THREE.Color(targetColor).getHexString()) {
                        m.color.set(targetColor);
                    }
                }
            }
            // Меняем курсор на «руку» когда мышь над стрелкой (Fusion-style).
            if (!draggingRef.current) {
                dom.style.cursor = (axisIdx == null) ? '' : 'grab';
            }
        };

        // Экранная позиция центра gizmo (в пикселях).
        const projectCenter = () => {
            const rect = dom.getBoundingClientRect();
            const p = positionRef.current;
            const w = new THREE.Vector3(p[0], p[1], p[2]).project(camera);
            return {
                x: rect.left + (w.x + 1) / 2 * rect.width,
                y: rect.top + (1 - w.y) / 2 * rect.height,
            };
        };

        // Старт drag'а кольца поворота вокруг оси ringIdx.
        const startRingDrag = (e, ringIdx) => {
            const center = projectCenter();
            const a0 = Math.atan2(e.clientY - center.y, e.clientX - center.x);
            let prevControlsEnabled = true;
            if (controls && 'enabled' in controls) {
                prevControlsEnabled = controls.enabled;
                controls.enabled = false;
            }
            const pf = useCadStore.getState().pendingFeature;
            draggingRef.current = {
                mode: 'rotate',
                ringIdx,
                center,
                startAngle: a0,
                startRot: [pf?.rx || 0, pf?.ry || 0, pf?.rz || 0],
                pointerId: e.pointerId,
                prevControlsEnabled,
                prevCursor: dom.style.cursor,
            };
            dom.style.cursor = 'grabbing';
            try { dom.setPointerCapture(e.pointerId); } catch (_) {}
        };

        const onPointerDown = (e) => {
            if (!groupRef.current?.visible) return;
            if (e.button !== 0) return;

            // Сначала пробуем кольцо поворота (оно поверх/рядом со стрелками).
            const ringIdx = hitTestRing(e.clientX, e.clientY);
            if (ringIdx != null) {
                e.stopPropagation();
                e.stopImmediatePropagation();
                e.preventDefault();
                startRingDrag(e, ringIdx);
                return;
            }

            const axisIdx = hitTestArrow(e.clientX, e.clientY);
            if (axisIdx == null) return;
            // Это мы — ловим drag, ВСЁ, OrbitControls сюда не пускаем.
            e.stopPropagation();
            e.stopImmediatePropagation();
            e.preventDefault();

            // axisScreen — направление оси на экране, в пикселях.
            const rect = dom.getBoundingClientRect();
            const project = (worldX, worldY, worldZ) => {
                const w = new THREE.Vector3(worldX, worldY, worldZ);
                w.project(camera);
                return {
                    x: rect.left + (w.x + 1) / 2 * rect.width,
                    y: rect.top + (1 - w.y) / 2 * rect.height,
                };
            };
            const pos = positionRef.current;
            const O = project(pos[0], pos[1], pos[2]);
            const A = project(
                pos[0] + (axisIdx === 0 ? 1 : 0),
                pos[1] + (axisIdx === 1 ? 1 : 0),
                pos[2] + (axisIdx === 2 ? 1 : 0),
            );
            const sx = A.x - O.x, sy = A.y - O.y;
            const slen = Math.sqrt(sx * sx + sy * sy) || 1;

            // Блокируем OrbitControls и сохраняем чтобы вернуть в pointer-up.
            let prevControlsEnabled = true;
            if (controls && 'enabled' in controls) {
                prevControlsEnabled = controls.enabled;
                controls.enabled = false;
            }

            // Какую операцию редактируем — primitive или moveCopy?
            // Для moveCopy используем зафиксированный initialCenter из
            // pendingFeature (а не пересчитываем от текущего mesh — он
            // сдвинут live-preview'ом и дал бы рассинхрон).
            const pf = useCadStore.getState().pendingFeature;
            const isMoveMode = pf?.kind === 'moveCopy';
            const bcRef = isMoveMode ? (pf.initialCenter || [0, 0, 0]) : [0, 0, 0];
            draggingRef.current = {
                axisIdx,
                mode: isMoveMode ? 'moveCopy' : 'primitive',
                bodyCenter: bcRef,
                startPos: [...pos],
                clientX: e.clientX,
                clientY: e.clientY,
                axisScreen: { x: sx / slen, y: sy / slen },
                mmPerPx: 1 / slen,
                pointerId: e.pointerId,
                prevControlsEnabled,
                prevCursor: dom.style.cursor,
            };
            dom.style.cursor = 'grabbing';
            // Включаем drag-line: оба конца совпадают в стартовой точке
            const dl = dragLineRef.current;
            if (dl) {
                const arr = dl.geometry.attributes.position.array;
                arr[0] = pos[0]; arr[1] = pos[1]; arr[2] = pos[2];
                arr[3] = pos[0]; arr[4] = pos[1]; arr[5] = pos[2];
                dl.geometry.attributes.position.needsUpdate = true;
                dl.computeLineDistances();
                dl.visible = true;
            }
            try { dom.setPointerCapture(e.pointerId); } catch (_) {}

            // Уведомляем DOM-overlay (Fusion-style инпут с величиной)
            const onStart = window.__kubikonCadGizmoDragStart;
            if (typeof onStart === 'function') {
                try {
                    onStart({
                        axisIdx,
                        startValue: pos[axisIdx],
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                } catch (_) {}
            }
        };

        const onPointerMoveHover = (e) => {
            if (!groupRef.current?.visible) return;
            if (draggingRef.current) return;  // пока тащим — не меняем hover
            const axisIdx = hitTestArrow(e.clientX, e.clientY);
            setHovered(axisIdx);
        };

        const onPointerMoveDrag = (e) => {
            const d = draggingRef.current;
            if (!d) return;
            e.stopPropagation();
            e.stopImmediatePropagation();
            e.preventDefault();

            // === Режим поворота (drag кольца) ===
            if (d.mode === 'rotate') {
                const a = Math.atan2(e.clientY - d.center.y, e.clientX - d.center.x);
                let deltaDeg = (a - d.startAngle) * 180 / Math.PI;
                // Нормализуем в (-180, 180]
                while (deltaDeg > 180) deltaDeg -= 360;
                while (deltaDeg <= -180) deltaDeg += 360;
                // С учётом ориентации взгляда камеры относительно оси — знак
                // может инвертироваться; для предсказуемости оставляем как есть.
                const rot = [...d.startRot];
                rot[d.ringIdx] = Math.round((d.startRot[d.ringIdx] + deltaDeg) * 10) / 10;
                const key = ['rx', 'ry', 'rz'][d.ringIdx];
                updatePendingFeature({ [key]: rot[d.ringIdx] });
                return;
            }

            const dx = e.clientX - d.clientX;
            const dy = e.clientY - d.clientY;
            const proj = dx * d.axisScreen.x + dy * d.axisScreen.y;
            const delta = proj * d.mmPerPx;
            const next = [...d.startPos];
            next[d.axisIdx] = Math.round((d.startPos[d.axisIdx] + delta) * 100) / 100;

            // Сразу двигаем group (без React-rerender'а — мгновенно)
            positionRef.current = next;
            if (groupRef.current) {
                groupRef.current.position.set(next[0], next[1], next[2]);
            }
            // Обновляем drag-line: второй конец = текущая позиция
            const dl = dragLineRef.current;
            if (dl && dl.visible) {
                const arr = dl.geometry.attributes.position.array;
                arr[3] = next[0]; arr[4] = next[1]; arr[5] = next[2];
                dl.geometry.attributes.position.needsUpdate = true;
                dl.computeLineDistances();
            }
            // Live-preview: для primitive дёргаем callback PrimitiveForm,
            // для moveCopy обновляем dx/dy/dz в store (его useEffect
            // запустит worker.moveOrCopyBody с preview=true).
            if (d.mode === 'primitive') {
                const live = window.__kubikonCadPrimitiveLive;
                if (typeof live === 'function') {
                    try { live({ position: next }); } catch (_) {}
                }
            } else if (d.mode === 'moveCopy') {
                const deltaXYZ = [
                    next[0] - d.bodyCenter[0],
                    next[1] - d.bodyCenter[1],
                    next[2] - d.bodyCenter[2],
                ];
                updatePendingFeature({
                    dx: Math.round(deltaXYZ[0] * 100) / 100,
                    dy: Math.round(deltaXYZ[1] * 100) / 100,
                    dz: Math.round(deltaXYZ[2] * 100) / 100,
                });
            }
            // Обновляем DOM-overlay с величиной
            const onMove = window.__kubikonCadGizmoDragMove;
            if (typeof onMove === 'function') {
                try {
                    onMove({
                        axisIdx: d.axisIdx,
                        currentValue: next[d.axisIdx],
                        startValue: d.startPos[d.axisIdx],
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                } catch (_) {}
            }
        };

        const onPointerUp = (e) => {
            const d = draggingRef.current;
            if (!d) return;
            draggingRef.current = null;
            // Возвращаем OrbitControls
            if (controls && 'enabled' in controls) {
                controls.enabled = d.prevControlsEnabled;
            }
            // Возвращаем cursor
            dom.style.cursor = d.prevCursor || '';
            // Скрываем drag-line
            if (dragLineRef.current) dragLineRef.current.visible = false;
            try { dom.releasePointerCapture(d.pointerId); } catch (_) {}
            // Закрываем DOM-overlay
            const onEnd = window.__kubikonCadGizmoDragEnd;
            if (typeof onEnd === 'function') {
                try { onEnd({ axisIdx: d.axisIdx, finalValue: positionRef.current[d.axisIdx] }); } catch (_) {}
            }
            // Записываем финальное состояние в store (одна запись на drag).
            // Для primitive — position; для moveCopy — dx/dy/dz уже записаны
            // в onMove, дополнительно ничего делать не надо.
            if (d.mode === 'primitive') {
                updatePendingFeature({ position: positionRef.current });
            }
        };

        // ВАЖНО: OrbitControls слушает pointermove/pointerup на document
        // (см. three/examples/.../OrbitControls.js) — поэтому ставим
        // наши move/up листенеры тоже на document с capture:true,
        // чтобы перехватить событие РАНЬШЕ OrbitControls.
        dom.addEventListener('pointerdown', onPointerDown, { capture: true });
        dom.addEventListener('pointermove', onPointerMoveHover);
        const docEl = dom.ownerDocument || document;
        docEl.addEventListener('pointermove', onPointerMoveDrag, { capture: true });
        docEl.addEventListener('pointerup', onPointerUp, { capture: true });
        docEl.addEventListener('pointercancel', onPointerUp, { capture: true });
        return () => {
            dom.removeEventListener('pointerdown', onPointerDown, { capture: true });
            dom.removeEventListener('pointermove', onPointerMoveHover);
            docEl.removeEventListener('pointermove', onPointerMoveDrag, { capture: true });
            docEl.removeEventListener('pointerup', onPointerUp, { capture: true });
            docEl.removeEventListener('pointercancel', onPointerUp, { capture: true });
        };
    }, [camera, gl, controls, updatePendingFeature]);

    return null;  // вся отрисовка через imperative scene.add
};

export default TransformGizmo;
